import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  Search,
  MapPin,
  Navigation,
  Settings,
  Store,
  UtensilsCrossed,
  ShoppingCart,
  Sparkles,
  Shirt,
  Monitor,
  Home as HomeIcon,
  Dumbbell,
  Baby,
  HeartPulse,
  Wrench,
} from 'lucide-react';
import type { LatLngTuple } from 'leaflet';
import type { Location } from '../types';
import './MyVibeModal.css';

type FilterType =
  | 'all'
  | 'centres'
  | 'shops'
  | 'restaurants'
  | 'supermarkets'
  | 'fashion'
  | 'beauty'
  | 'electronics'
  | 'home'
  | 'sports'
  | 'kids'
  | 'health'
  | 'services';

interface MyVibeModalProps {
  isOpen: boolean;
  onClose: () => void;
  allLocations: Record<string, any> | Location[];
  userLocation: LatLngTuple | null;
  onSelectLocation: (locationId: string) => void;
  onSelectShop: (locationId: string, shopId: string) => void;
}

interface NormalizedLocation extends Omit<Location, 'shops'> {
  shops?: Record<string, any> | any[];
}

interface CentreResult {
  id: string;
  name: string;
  latitude?: number;
  longitude?: number;
  distanceKm: number | null;
  totalShops: number;
  previewShops: string[];
  raw: NormalizedLocation;
}

interface ShopResult {
  id: string;
  name: string;
  category: string;
  locationId: string;
  locationName: string;
  shopKey: string; // The actual key used in the shops object for lookup
  derivedCategories: string[];
  latitude?: number;
  longitude?: number;
  distanceKm: number | null;
  raw: any;
}

const RESTAURANT_KEYWORDS = [
  'restaurant',
  'dining',
  'food court',
  'foodhall',
  'food hall',
  'food',
  'eat',
  'eatery',
  'cafe',
  'coffee',
  'espresso',
  'barista',
  'bar',
  'bistro',
  'pub',
  'tavern',
  'grill',
  'kitchen',
  'steak',
  'seafood',
  'sushi',
  'ramen',
  'izakaya',
  'yakitori',
  'korean bbq',
  'bbq',
  'dumpling',
  'noodle',
  'pasta',
  'pizza',
  'burger',
  'sandwich',
  'bakery',
  'patisserie',
  'dessert',
  'gelato',
  'ice cream',
  'confectionery',
  'takeaway',
  'take-away',
  'fast food',
  'drive thru',
  'juice',
  'smoothie',
  'bubble tea',
  'tea',
  'wine',
  'tapas',
  'burrito',
  'mexican',
  'thai',
  'indian',
  'chinese',
  'japanese',
  'korean',
  'vietnamese',
  'malaysian',
  'singaporean',
  'italian',
  'french',
  'spanish',
  'greek',
  'lebanese',
  'middle eastern',
  'african',
  'ethiopian',
  'peruvian',
  'argentinian',
  'turkish',
  'kebab',
  'shawarma',
  'poke',
  'vegan',
  'vegetarian',
  'gluten free',
  'gourmet',
  'fine dining',
];

const RESTAURANT_BRANDS = [
  'mcdonald',
  'kfc',
  'hungry jack',
  'subway',
  'red rooster',
  'grilld',
  'nando',
  'zambrero',
  'guzman',
  'mad mex',
  'starbucks',
  'gloria jean',
  'doughnut time',
  'boost juice',
  'chatime',
  'gong cha',
  'hudsons coffee',
  'pretzel',
  'betty',
  'five guys',
  'tgi fridays',
  'vietnamese street eats',
  'rolls pho',
  'din tai fung',
  'pappa rich',
];

const SUPERMARKET_KEYWORDS = [
  'supermarket',
  'grocery',
  'grocer',
  'market',
  'hypermarket',
  'fresh food',
  'butcher',
  'seafood market',
  'green grocer',
  'fruit market',
  'produce market',
  'deli',
  'delicatessen',
  'organic food',
  'wholesale',
  'wholesaler',
  'bulk foods',
];

const SUPERMARKET_BRANDS = [
  'coles',
  'woolworth',
  'aldi',
  'iga',
  'costco',
  'harris farm',
  'foodland',
  'foodworks',
  'supabarn',
  'friendly grocer',
  'spudshed',
  'city fresh',
  'farmer jack',
  'fruitologist',
  'grocer and grind',
  'marketplace',
  'central market',
];

const normalizeLabel = (label: string): string =>
  label
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const collectDescriptors = (shopData: any): string[] => {
  if (!shopData || typeof shopData !== 'object') return [];

  const descriptors: string[] = [];

  const descriptorFields = [
    shopData.category,
    shopData.categories,
    shopData.primaryCategory,
    shopData.primary_category,
    shopData.secondaryCategory,
    shopData.secondary_category,
    shopData.type,
    shopData.types,
    shopData.storeType,
    shopData.shopType,
    shopData.shop_type,
    shopData.format,
    shopData.industry,
    shopData.industryCategory,
    shopData.industry_category,
    shopData.vertical,
    shopData.segment,
    shopData.sector,
    shopData.businessType,
    shopData.businessTypes,
    shopData.business_type,
    shopData.business_types,
    shopData.cuisine,
    shopData.cuisines,
    shopData.foodType,
    shopData.foodTypes,
    shopData.subcategory,
    shopData.subcategories,
    shopData.categoryHierarchy,
    shopData.tags,
    shopData.keywords,
    shopData.labels,
    shopData.summary,
    shopData.description,
    shopData.specialty,
    shopData.specialties,
    shopData.features,
    shopData.attributes,
    shopData.offerings,
  ];

  const addValue = (value: any) => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach(addValue);
      return;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) descriptors.push(trimmed);
      return;
    }
    if (typeof value === 'object') {
      Object.values(value).forEach(addValue);
    }
  };

  descriptorFields.forEach(addValue);

  return Array.from(new Set(descriptors));
};

const FASHION_KEYWORDS = [
  'fashion',
  'clothing',
  'apparel',
  'wardrobe',
  'boutique',
  'dress',
  'suit',
  'shirt',
  'pants',
  'jeans',
  'skirt',
  'outerwear',
  'activewear',
  'lingerie',
  'sleepwear',
  'footwear',
  'shoe',
  'sneaker',
  'athleisure',
  'streetwear',
  'couture',
  'accessory',
  'handbag',
  'jewellery',
  'jewelry',
  'watch',
];

const FASHION_BRANDS = [
  'h&m',
  'zara',
  'uniqlo',
  'nike',
  'adidas',
  'lululemon',
  'foot locker',
  'cotton on',
  'decjuba',
  'country road',
  'seed heritage',
  'glassons',
  'r.m.williams',
  'peter alexander',
  'skechers',
  'athlete',
  'jd sports',
  'van heusen',
  'politix',
  'pandora',
];

const BEAUTY_KEYWORDS = [
  'beauty',
  'cosmetic',
  'skincare',
  'skin care',
  'makeup',
  'make-up',
  'fragrance',
  'perfume',
  'hair',
  'salon',
  'spa',
  'nail',
  'lash',
  'brow',
  'wellness',
  'dermal',
  'clinic',
  'aesthetic',
];

const BEAUTY_BRANDS = [
  'sephora',
  'mecca',
  'body shop',
  'lush',
  'priced',
  'hairhouse',
  'laser clinics',
  'endota',
  'ella bache',
  'blow dry bar',
];

const ELECTRONICS_KEYWORDS = [
  'electronics',
  'tech',
  'technology',
  'computer',
  'phone',
  'mobile',
  'tablet',
  'device',
  'gaming',
  'console',
  'digital',
  'camera',
  'audio',
  'vision',
  'sound',
  'electric',
  'appliance',
  'whitegoods',
  'home entertainment',
  'smart',
];

const ELECTRONICS_BRANDS = [
  'jb hi-fi',
  'harvey norman',
  'the good guys',
  'apple',
  'samsung',
  'microsoft',
  'eb games',
  'game stop',
  'sony',
  'lg',
  'bose',
];

const HOME_KEYWORDS = [
  'home',
  'living',
  'furniture',
  'decor',
  'interior',
  'kitchen',
  'bathroom',
  'bedroom',
  'manchester',
  'linen',
  'housewares',
  'cookware',
  'tableware',
  'lighting',
  'garden',
  'outdoor',
  'hardware',
  'diy',
  'renovation',
];

const HOME_BRANDS = [
  'ikea',
  'freedom',
  'fantastic furniture',
  'adairs',
  'bed bath n table',
  'habitania',
  'breville',
  'victoria basement',
  'house',
  'spotlight',
  'bunnings',
];

const SPORTS_KEYWORDS = [
  'sport',
  'sports',
  'fitness',
  'gym',
  'athletic',
  'outdoor',
  'recreation',
  'swim',
  'surf',
  'skate',
  'snow',
  'cycle',
  'bike',
  'teamwear',
  'sportswear',
  'active',
  'nutrition',
  'supplement',
];

const SPORTS_BRANDS = [
  'rebel',
  'decathlon',
  'platypus',
  'macpac',
  'mountain designs',
  '2xu',
  'nutrition warehouse',
  'asn',
];

const KIDS_KEYWORDS = [
  'children',
  'kids',
  'baby',
  'infant',
  'toys',
  'games',
  'play',
  'nursery',
  'maternity',
  'stroller',
  'pram',
  'lego',
  'learning',
  'education',
];

const KIDS_BRANDS = [
  'toys r us',
  'lego',
  'smiggle',
  'build-a-bear',
  'baby bunting',
  'kidstuff',
  'michael hill kids',
  'shoe warehouse kids',
];

const HEALTH_KEYWORDS = [
  'pharmacy',
  'chemist',
  'health',
  'wellness',
  'medical',
  'clinic',
  'optical',
  'optometrist',
  'audiology',
  'dentist',
  'physio',
  'chiro',
  'podiatry',
  'allied health',
  'therapy',
  'supplement',
  'vitamin',
  'nutrition',
];

const HEALTH_BRANDS = [
  'chemist warehouse',
  'priceline',
  'terrywhite',
  'amcal',
  'specsavers',
  'bailey nelson',
  'optical superstore',
  'hcf dental',
  'qoptics',
];

const SERVICES_KEYWORDS = [
  'service',
  'tailor',
  'repair',
  'dry clean',
  'alteration',
  'key cutting',
  'locksmith',
  'printing',
  'copy',
  'photo',
  'travel',
  'agency',
  'bank',
  'finance',
  'insurance',
  'legal',
  'real estate',
  'post',
  'parcel',
  'courier',
  'laundry',
  'pet',
  'grooming',
];

const SERVICES_BRANDS = [
  'auspost',
  'commonwealth bank',
  'westpac',
  'anz',
  'nab',
  'sharetea',
  'flight centre',
  'helloworld',
  'snap print',
  'mister minit',
  'shoe repairs',
  'snap fitness',
  'anytime fitness services',
];

const CATEGORY_LABELS: Record<string, string> = {
  restaurant: 'Restaurant',
  supermarket: 'Supermarket',
  fashion: 'Fashion & Apparel',
  beauty: 'Beauty & Wellness',
  electronics: 'Electronics & Tech',
  home: 'Home & Living',
  sports: 'Sports & Outdoors',
  kids: 'Kids & Toys',
  health: 'Health & Pharmacy',
  services: 'Services',
};

type LocationSuggestion = {
  id: string;
  label: string;
  subtitle?: string;
  coords: LatLngTuple;
};

const parseCoordinate = (value: unknown): number | null => {
  if (value === undefined || value === null) return null;
  const numeric = typeof value === 'string' ? parseFloat(value) : value;
  if (typeof numeric !== 'number' || Number.isNaN(numeric) || !Number.isFinite(numeric)) {
    return null;
  }
  return numeric;
};

const determineShopCategories = (shopName: string, baseCategory: string | undefined, descriptors: string[]): {
  displayCategory: string;
  derivedCategories: string[];
} => {
  const categories = new Set<string>();
  const targets = [shopName, baseCategory, ...descriptors]
    .filter(Boolean)
    .map((text) => text!.toString().toLowerCase());

  const combined = targets.join(' | ');

  const matchesAny = (keywords: string[]) => keywords.some((keyword) => combined.includes(keyword));

  if (matchesAny(RESTAURANT_KEYWORDS) || matchesAny(RESTAURANT_BRANDS)) {
    categories.add('restaurant');
  }

  if (matchesAny(SUPERMARKET_KEYWORDS) || matchesAny(SUPERMARKET_BRANDS)) {
    categories.add('supermarket');
  }

  if (matchesAny(FASHION_KEYWORDS) || matchesAny(FASHION_BRANDS)) {
    categories.add('fashion');
  }

  if (matchesAny(BEAUTY_KEYWORDS) || matchesAny(BEAUTY_BRANDS)) {
    categories.add('beauty');
  }

  if (matchesAny(ELECTRONICS_KEYWORDS) || matchesAny(ELECTRONICS_BRANDS)) {
    categories.add('electronics');
  }

  if (matchesAny(HOME_KEYWORDS) || matchesAny(HOME_BRANDS)) {
    categories.add('home');
  }

  if (matchesAny(SPORTS_KEYWORDS) || matchesAny(SPORTS_BRANDS)) {
    categories.add('sports');
  }

  if (matchesAny(KIDS_KEYWORDS) || matchesAny(KIDS_BRANDS)) {
    categories.add('kids');
  }

  if (matchesAny(HEALTH_KEYWORDS) || matchesAny(HEALTH_BRANDS)) {
    categories.add('health');
  }

  if (matchesAny(SERVICES_KEYWORDS) || matchesAny(SERVICES_BRANDS)) {
    categories.add('services');
  }

  let displayCategory = baseCategory;
  let displaySlug = typeof baseCategory === 'string' ? baseCategory.trim().toLowerCase() : undefined;

  const baseIsGeneric = !displaySlug || ['shop', 'retail store', 'store', 'retail', 'retailer'].includes(displaySlug);

  if (baseIsGeneric) {
    const priorityOrder = ['restaurant', 'supermarket', 'fashion', 'beauty', 'electronics', 'home', 'sports', 'kids', 'health', 'services'];
    const prioritized = priorityOrder.find((category) => categories.has(category));

    if (prioritized) {
      displaySlug = prioritized;
      displayCategory = CATEGORY_LABELS[prioritized] || normalizeLabel(prioritized);
    } else if (descriptors.length > 0) {
      displayCategory = normalizeLabel(descriptors[0]);
      displaySlug = displayCategory?.toLowerCase();
    } else {
      displayCategory = 'Shop';
      displaySlug = 'shop';
    }
  }

  const derivedList = Array.from(categories);

  return {
    displayCategory: CATEGORY_LABELS[displaySlug ?? ''] || normalizeLabel(displayCategory || 'Shop'),
    derivedCategories: derivedList,
  };
};

const normalizeLocations = (locations: Record<string, any> | Location[]): NormalizedLocation[] => {
  if (!locations) return [];
  if (Array.isArray(locations)) {
    return locations.map((loc) => ({
      _id: loc._id || loc.location || String(loc),
      location: loc.location || loc._id || String(loc),
      latitude: loc.latitude,
      longitude: loc.longitude,
      category: loc.category || 'Centre',
      shops: (loc as any).shops,
      ...loc,
    }));
  }

  return Object.entries(locations).map(([key, loc]) => ({
    _id: key,
    location: key,
    latitude: loc.latitude,
    longitude: loc.longitude,
    category: loc.category || 'Centre',
    shops: loc.shops,
    ...loc,
  }));
};

const calculateDistance = (lat1: number, lon1: number, lat2?: number, lon2?: number): number | null => {
  if (lat2 === undefined || lon2 === undefined) return null;
  const R = 6371; // km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const isRestaurantCategory = (category: string) => {
  const value = category?.toLowerCase() || '';
  return [
    'restaurant',
    'dining',
    'food court',
    'food',
    'eat',
    'cafe',
    'bar',
    'grill',
    'kitchen',
  ].some((keyword) => value.includes(keyword));
};

const isSupermarketCategory = (category: string) => {
  const value = category?.toLowerCase() || '';
  return ['supermarket', 'grocery', 'market', 'grocer', 'fresh food'].some((keyword) => value.includes(keyword));
};

const formatDistance = (distanceKm: number | null) => {
  if (distanceKm === null || Number.isNaN(distanceKm)) return 'Distance unknown';
  if (distanceKm < 1) {
    return `${Math.round(distanceKm * 1000)} m`;
  }
  return `${distanceKm.toFixed(1)} km`;
};

const getShopName = (shopId: string, shopData: any) => {
  // If shopData is a string, use it directly
  if (typeof shopData === 'string') return shopData;
  
  // If shopData is an object, try multiple name fields
  if (shopData && typeof shopData === 'object') {
    return shopData.name || shopData.storeName || shopData.title || shopData.shopName || shopId;
  }
  
  // Fallback to shopId (which might be the shop name itself)
  return shopId;
};

const getShopCategory = (shopData: any) => {
  if (!shopData) return 'Shop';
  return shopData.category || shopData.type || 'Shop';
};

const MyVibeModal: React.FC<MyVibeModalProps> = ({
  isOpen,
  onClose,
  allLocations,
  userLocation,
  onSelectLocation,
  onSelectShop,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [customLocation, setCustomLocation] = useState<{ label: string; coords: LatLngTuple } | null>(null);
  const [showLocationSettings, setShowLocationSettings] = useState(false);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        const storedScroll = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, parseInt(storedScroll || '0') * -1);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setActiveFilter('all');
      setShowLocationSettings(false);
      setLocationSearchTerm('');
    }
  }, [isOpen]);

  const locationsArray = useMemo(() => normalizeLocations(allLocations), [allLocations]);

  const activeBaseCoords: LatLngTuple | null = customLocation?.coords || userLocation || null;
  const activeLocationLabel = customLocation?.label || (userLocation ? 'Current location' : null);
  const shouldPromptLocation = !activeBaseCoords;
  const shouldShowLocationEditor = showLocationSettings || shouldPromptLocation;

  useEffect(() => {
    if (shouldPromptLocation) {
      setShowLocationSettings(true);
    }
  }, [shouldPromptLocation]);

  const locationSuggestions = useMemo<LocationSuggestion[]>(() => {
    const term = locationSearchTerm.trim().toLowerCase();
    const maxResults = term ? 12 : 6;
    const seen = new Set<string>();
    const suggestions: LocationSuggestion[] = [];

    for (const loc of locationsArray) {
      const lat = parseCoordinate(loc.latitude);
      const lon = parseCoordinate(loc.longitude);
      if (lat === null || lon === null) continue;

      const suburb = (loc as any)?.suburb as string | undefined;
      const state = (loc as any)?.state as string | undefined;
      const city = (loc as any)?.city as string | undefined;
      const locationName = loc.location || loc._id || '';
      const label = suburb ? `${suburb}${state ? `, ${state}` : ''}` : locationName;

      const searchBlob = [label, locationName, loc._id, suburb, state, city]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (term && !searchBlob.includes(term)) continue;

      const suggestionId = loc._id || locationName || `${lat},${lon}`;
      if (seen.has(suggestionId)) continue;
      seen.add(suggestionId);

      suggestions.push({
        id: suggestionId,
        label,
        subtitle:
          locationName && locationName.toLowerCase() !== label.toLowerCase() ? locationName : undefined,
        coords: [lat, lon],
      });

      if (suggestions.length >= maxResults) {
        break;
      }
    }

    return suggestions;
  }, [locationsArray, locationSearchTerm]);

  const handleLocationSelect = (suggestion: LocationSuggestion) => {
    setCustomLocation({ label: suggestion.label, coords: suggestion.coords });
    setShowLocationSettings(false);
    setLocationSearchTerm('');
  };

  const handleUseCurrentBaseLocation = () => {
    if (!userLocation) return;
    setCustomLocation(null);
    setShowLocationSettings(false);
    setLocationSearchTerm('');
  };

  const centreResults = useMemo<CentreResult[]>(() => {
    return locationsArray
      .map((loc) => {
        const shops = loc.shops && typeof loc.shops === 'object' ? loc.shops : {};
        const shopKeys = Array.isArray(shops) ? shops : Object.keys(shops || {});
        const previewShops = Array.isArray(shops)
          ? shops.slice(0, 3).map((value) => {
              if (typeof value === 'string') return value;
              if (value && typeof value === 'object') {
                return getShopName('', value) || 'Shop';
              }
              return 'Shop';
            })
          : Object.keys(shops || {})
              .slice(0, 3)
              .map((key) => getShopName(key, shops[key]));

        const totalShops = Array.isArray(shops) ? shops.length : Object.keys(shops || {}).length;

        const lat = parseCoordinate(loc.latitude);
        const lon = parseCoordinate(loc.longitude);
        const distanceKm =
          activeBaseCoords && lat !== null && lon !== null
            ? calculateDistance(activeBaseCoords[0], activeBaseCoords[1], lat, lon)
            : null;

        return {
          id: loc._id || loc.location,
          name: loc.location || loc._id,
          latitude: lat ?? undefined,
          longitude: lon ?? undefined,
          distanceKm,
          totalShops,
          previewShops,
          raw: loc,
        };
      })
      .sort((a, b) => {
        if (a.distanceKm === null && b.distanceKm === null) return a.name.localeCompare(b.name);
        if (a.distanceKm === null) return 1;
        if (b.distanceKm === null) return -1;
        return a.distanceKm - b.distanceKm;
      });
  }, [locationsArray, activeBaseCoords]);

  const shopResults = useMemo<ShopResult[]>(() => {
    const results: ShopResult[] = [];

    locationsArray.forEach((loc) => {
      const shops = loc.shops && typeof loc.shops === 'object' ? loc.shops : {};
      const locationLat = parseCoordinate(loc.latitude);
      const locationLon = parseCoordinate(loc.longitude);
      if (Array.isArray(shops)) {
        shops.forEach((shop, index) => {
          if (!shop) return;
          
          // Handle string shops (shop name is the value itself)
          if (typeof shop === 'string') {
            const distanceKm =
              activeBaseCoords && locationLat !== null && locationLon !== null
                ? calculateDistance(activeBaseCoords[0], activeBaseCoords[1], locationLat, locationLon)
                : null;
            const { displayCategory, derivedCategories } = determineShopCategories(shop, undefined, [shop]);
            results.push({
              id: String(index),
              name: shop,
              category: displayCategory,
              locationId: loc._id || loc.location,
              locationName: loc.location || loc._id,
              shopKey: shop, // For array shops, the shop name is the key
              derivedCategories,
              latitude: locationLat ?? undefined,
              longitude: locationLon ?? undefined,
              distanceKm,
              raw: shop,
            });
            return;
          }
          
          // Handle object shops in array
          const name = getShopName(String(index), shop);
          const baseCategory = getShopCategory(shop);
          const shopLat = parseCoordinate((shop as any)?.latitude);
          const shopLon = parseCoordinate((shop as any)?.longitude);
          const resolvedLat = shopLat ?? locationLat;
          const resolvedLon = shopLon ?? locationLon;
          const distanceKm =
            activeBaseCoords && resolvedLat !== null && resolvedLon !== null
              ? calculateDistance(activeBaseCoords[0], activeBaseCoords[1], resolvedLat, resolvedLon)
              : null;
          const descriptors = collectDescriptors(shop);
          const { displayCategory, derivedCategories } = determineShopCategories(name, baseCategory, descriptors);
          // For array shops with objects, use the shop name as the key (matching search dropdown behavior)
          const shopKey = name;

          results.push({
            id: shop.id || shop._id || String(index),
            name,
            category: displayCategory,
            locationId: loc._id || loc.location,
            locationName: loc.location || loc._id,
            shopKey,
            derivedCategories,
            latitude: resolvedLat ?? undefined,
            longitude: resolvedLon ?? undefined,
            distanceKm,
            raw: shop,
          });
        });
      } else {
        Object.entries(shops || {}).forEach(([shopId, shopData]) => {
          // Handle case where shopData might be null/undefined but shopId is the name
          if (!shopData) {
            // If shopId looks like a shop name (not just an ID), use it
            if (shopId && shopId.length > 0) {
              const distanceKm =
                activeBaseCoords && locationLat !== null && locationLon !== null
                  ? calculateDistance(activeBaseCoords[0], activeBaseCoords[1], locationLat, locationLon)
                  : null;
              const { displayCategory, derivedCategories } = determineShopCategories(shopId, undefined, [shopId]);
              results.push({
                id: shopId,
                name: shopId, // Use shopId as name
                category: displayCategory,
                locationId: loc._id || loc.location,
                locationName: loc.location || loc._id,
                shopKey: shopId, // For object shops, shopId is the key
                derivedCategories,
                latitude: locationLat ?? undefined,
                longitude: locationLon ?? undefined,
                distanceKm,
                raw: shopId,
              });
            }
            return;
          }
          
          const name = getShopName(shopId, shopData);
          const baseCategory = getShopCategory(shopData);
          const shopLat = parseCoordinate((shopData as any)?.latitude);
          const shopLon = parseCoordinate((shopData as any)?.longitude);
          const resolvedLat = shopLat ?? locationLat;
          const resolvedLon = shopLon ?? locationLon;
          const distanceKm =
            activeBaseCoords && resolvedLat !== null && resolvedLon !== null
              ? calculateDistance(activeBaseCoords[0], activeBaseCoords[1], resolvedLat, resolvedLon)
              : null;
          const descriptors = collectDescriptors(shopData);
          const { displayCategory, derivedCategories } = determineShopCategories(name, baseCategory, descriptors);

          results.push({
            id: shopId,
            name,
            category: displayCategory,
            locationId: loc._id || loc.location,
            locationName: loc.location || loc._id,
            shopKey: shopId, // For object shops, shopId is the key in the shops object
            derivedCategories,
            latitude: resolvedLat ?? undefined,
            longitude: resolvedLon ?? undefined,
            distanceKm,
            raw: shopData,
          });
        });
      }
    });

    return results.sort((a, b) => {
      if (a.distanceKm === null && b.distanceKm === null) return a.name.localeCompare(b.name);
      if (a.distanceKm === null) return 1;
      if (b.distanceKm === null) return -1;
      return a.distanceKm - b.distanceKm;
    });
  }, [locationsArray, activeBaseCoords]);

  const query = searchQuery.trim().toLowerCase();

  const filteredCentres = useMemo(() => {
    if (
      [
        'shops',
        'restaurants',
        'supermarkets',
        'fashion',
        'beauty',
        'electronics',
        'home',
        'sports',
        'kids',
        'health',
        'services',
      ].includes(activeFilter)
    ) {
      return [];
    }

    if (!query) return centreResults;

    return centreResults.filter((centre) => {
      const matchesName = centre.name.toLowerCase().includes(query);
      const matchesShop = centre.previewShops.some((shop) => shop.toLowerCase().includes(query));
      return matchesName || matchesShop;
    });
  }, [centreResults, activeFilter, query]);

  const filteredShops = useMemo(() => {
    return shopResults.filter((shop) => {
      const derivedSet = new Set(shop.derivedCategories.map((slug) => slug.toLowerCase()));
      const matchesDerived = (slug: string) => derivedSet.has(slug);

      const matchesCategoryFilter = (() => {
        switch (activeFilter) {
          case 'all':
            return true;
          case 'shops':
            return true;
          case 'centres':
            return false;
          case 'restaurants':
            return matchesDerived('restaurant') || isRestaurantCategory(shop.category);
          case 'supermarkets':
            return matchesDerived('supermarket') || isSupermarketCategory(shop.category);
          case 'fashion':
          case 'beauty':
          case 'electronics':
          case 'home':
          case 'sports':
          case 'kids':
          case 'health':
          case 'services':
            return matchesDerived(activeFilter);
          default:
            return true;
        }
      })();

      if (!matchesCategoryFilter) {
        return false;
      }

      if (!query) {
        if (activeFilter === 'centres') return false;
        return true;
      }

      const lowerName = shop.name?.toLowerCase() || '';
      const lowerLocation = shop.locationName?.toLowerCase() || '';
      const lowerCategory = shop.category?.toLowerCase() || '';
      const matchesDerivedQuery = Array.from(derivedSet).some((slug) => slug.includes(query));
      const matchesQuery =
        lowerName.includes(query) ||
        lowerLocation.includes(query) ||
        lowerCategory.includes(query) ||
        matchesDerivedQuery;

      return matchesQuery;
    });
  }, [shopResults, activeFilter, query]);

  const displayedShopResults = activeFilter === 'all' && !query
    ? shopResults.slice(0, 8)
    : filteredShops;

  const categoryFilters: Array<{ id: FilterType; label: string; icon: React.ReactNode }> = [
    { id: 'all', label: 'All', icon: <Sparkles size={18} /> },
    { id: 'centres', label: 'Centres', icon: <MapPin size={18} /> },
    { id: 'shops', label: 'Shops', icon: <Store size={18} /> },
    { id: 'restaurants', label: 'Restaurants', icon: <UtensilsCrossed size={18} /> },
    { id: 'supermarkets', label: 'Supermarkets', icon: <ShoppingCart size={18} /> },
    { id: 'fashion', label: 'Fashion', icon: <Shirt size={18} /> },
    { id: 'beauty', label: 'Beauty', icon: <Sparkles size={18} /> },
    { id: 'electronics', label: 'Electronics', icon: <Monitor size={18} /> },
    { id: 'home', label: 'Home & Living', icon: <HomeIcon size={18} /> },
    { id: 'sports', label: 'Sports', icon: <Dumbbell size={18} /> },
    { id: 'kids', label: 'Kids & Toys', icon: <Baby size={18} /> },
    { id: 'health', label: 'Health', icon: <HeartPulse size={18} /> },
    { id: 'services', label: 'Services', icon: <Wrench size={18} /> },
  ];

  const handleLocationClick = (centre: CentreResult) => {
    onSelectLocation(centre.id);
    onClose();
  };

  const handleShopClick = (shop: ShopResult) => {
    onSelectShop(shop.locationId, shop.shopKey);
    onClose();
  };

  const hasCentresToShow = filteredCentres.length > 0;
  const hasShopResults = displayedShopResults.length > 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="myvibe-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="myvibe-modal"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 26, stiffness: 320 }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="myvibe-header">
              <div className="myvibe-title-block">
                <h2>My Vibe</h2>
              </div>
              <div className="myvibe-header-actions">
                <button
                  type="button"
                  className={`location-settings-button ${shouldShowLocationEditor ? 'active' : ''}`}
                  onClick={() => setShowLocationSettings((prev) => !prev)}
                  aria-label="Set location preferences"
                >
                  <Settings size={18} />
                </button>
                <button className="close-button" onClick={onClose} aria-label="Close My Vibe">
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="myvibe-content">
              <div className="myvibe-search-block">
                <div className="search-input-wrapper">
                  <Search size={18} />
                  <input
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search for centres or shops..."
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                  />
                  {userLocation && <Navigation size={18} className="location-indicator" />}
                </div>
                <div className="popular-categories-scroll" role="tablist" aria-label="Categories">
                  {categoryFilters.map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      className={`popular-chip ${activeFilter === category.id ? 'active' : ''}`}
                      onClick={() =>
                        setActiveFilter((prev) => (prev === category.id ? 'shops' : category.id))
                      }
                    >
                      {category.icon}
                      <span>{category.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className={`active-location-banner ${shouldPromptLocation ? 'warning' : ''}`} role="status">
                <div className="location-chip">
                  <MapPin size={18} />
                  <div className="location-chip-text">
                    <span className="chip-label">
                      {shouldPromptLocation ? 'Set your location' : 'Suggestions near'}
                    </span>
                    <span className="chip-value">
                      {activeLocationLabel || 'Add suburb or enable current location'}
                    </span>
                  </div>
                </div>

                <div className="location-actions">
                  {userLocation && (
                    <button
                      type="button"
                      className="location-use-current-inline"
                      onClick={handleUseCurrentBaseLocation}
                    >
                      <Navigation size={16} />
                      <span>Use current location</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="location-edit-button"
                    onClick={() => setShowLocationSettings(true)}
                  >
                    <Settings size={16} />
                    <span>{shouldPromptLocation ? 'Set location' : 'Change'}</span>
                  </button>
                </div>

                {shouldShowLocationEditor && (
                  <div className="location-settings-card">
                    <div className="location-settings-header">
                      <div>
                        <h3>Choose your area</h3>
                        <p>Select a suburb or centre to tailor suggestions near you.</p>
                      </div>
                    </div>
                    <div className="location-search-input">
                      <Search size={16} />
                      <input
                        value={locationSearchTerm}
                        onChange={(event) => setLocationSearchTerm(event.target.value)}
                        placeholder="Search suburb or shopping centre..."
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                      />
                    </div>
                    <div className="location-suggestions">
                      {locationSuggestions.length > 0 ? (
                        locationSuggestions.map((suggestion) => (
                          <button
                            key={suggestion.id}
                            type="button"
                            className="location-suggestion"
                            onClick={() => handleLocationSelect(suggestion)}
                          >
                            <div className="suggestion-main">
                              <span className="suggestion-title">{suggestion.label}</span>
                              {suggestion.subtitle && (
                                <span className="suggestion-subtitle">{suggestion.subtitle}</span>
                              )}
                            </div>
                            <span className="suggestion-action">Use</span>
                          </button>
                        ))
                      ) : (
                        <div className="location-empty">No matching locations found.</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {hasCentresToShow && (
                <section className="myvibe-section">
                  <div className="section-header">
                    <div>
                      <h3>{activeFilter === 'centres' ? 'Centres' : 'Nearby Centres'}</h3>
                      {activeBaseCoords && (
                        <span className="section-subtitle">Ranked by proximity</span>
                      )}
                    </div>
                    <span className="section-count">{filteredCentres.length} locations</span>
                  </div>

                  <div className="centre-grid">
                    {filteredCentres.slice(0, 8).map((centre) => (
                      <button
                        key={centre.id}
                        className="centre-card"
                        type="button"
                        onClick={() => handleLocationClick(centre)}
                      >
                        <div className="centre-card-header">
                          <h4>{centre.name}</h4>
                          {centre.distanceKm !== null && (
                            <span className="distance-pill">{formatDistance(centre.distanceKm)}</span>
                          )}
                        </div>
                        <div className="centre-card-body">
                          <div className="centre-meta">
                            <MapPin size={14} />
                            <span>{centre.totalShops} shops</span>
                          </div>
                          {centre.previewShops.length > 0 && (
                            <div className="centre-preview-shops">
                              {centre.previewShops.map((shop) => (
                                <span key={shop}>{shop}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </section>
              )}

              <section className="myvibe-section">
                <div className="section-header">
                  <div>
                    <h3>
                      {activeFilter === 'restaurants'
                        ? 'Nearby Restaurants'
                        : activeFilter === 'supermarkets'
                        ? 'Nearby Supermarkets'
                        : 'Discover Shops'}
                    </h3>
                    {activeBaseCoords && (
                      <span className="section-subtitle">Sorted by distance</span>
                    )}
                  </div>
                  <span className="section-count">{displayedShopResults.length} matches</span>
                </div>

                {hasShopResults ? (
                  <div className="shop-grid">
                    {displayedShopResults.map((shop) => (
                      <button
                        key={`${shop.locationId}-${shop.id}`}
                        className="centre-card shop-card"
                        type="button"
                        onClick={() => handleShopClick(shop)}
                      >
                        <div className="centre-card-header">
                          <h4>{shop.name}</h4>
                          {shop.distanceKm !== null && (
                            <span className="distance-pill">{formatDistance(shop.distanceKm)}</span>
                          )}
                        </div>
                        <div className="centre-card-body">
                          <div className="shop-category-pill">
                            <Store size={14} />
                            <span>{shop.category || 'Shop'}</span>
                          </div>
                          <div className="centre-meta shop-location-meta">
                            <MapPin size={14} />
                            <span>{shop.locationName}</span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No places match your filters yet.</p>
                    <span>Try adjusting your filters or searching for another keyword.</span>
                  </div>
                )}
              </section>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MyVibeModal;

